<template>
    <v-app>
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                                Search Form
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-form @submit.prevent="loadReport">
                                    <v-row class="px-2 pt-4 pb-0">
                                        <v-col lg="2" md="2" cols="12">
                                            <v-autocomplete label="Sewing Line" variant="outlined" density="compact"
                                                bg-color="grey-lighten-4" v-model="line_id.value.value"
                                                :items="sewingLineList" :item-title="item => item.code"
                                                :item-value="item => item.id"
                                                :error-messages="line_id.errorMessage.value" autocomplete="off"
                                                clearable></v-autocomplete>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-autocomplete label="Style" variant="outlined" density="compact"
                                                bg-color="grey-lighten-4" v-model="style_id.value.value"
                                                :items="styleList" :item-title="item => item.code || item.name"
                                                :item-value="item => item.id"
                                                :error-messages="style_id.errorMessage.value" autocomplete="off"
                                                clearable></v-autocomplete>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-date-input input-format="dd/mm/yyyy" label="From Date" density="compact" prepend-icon=""
                                                prepend-inner-icon="mdi-calendar-month" variant="outlined"
                                                bg-color="grey-lighten-4" v-model="from_date.value.value"
                                                :error-messages="from_date.errorMessage.value" autocomplete="off"
                                                clearable></v-date-input>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-date-input input-format="dd/mm/yyyy" label="To Date" density="compact" prepend-icon=""
                                                prepend-inner-icon="mdi-calendar-month" variant="outlined"
                                                bg-color="grey-lighten-4" v-model="to_date.value.value"
                                                :error-messages="to_date.errorMessage.value" autocomplete="off"
                                                clearable></v-date-input>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-btn class="bg-brand w-15" type="submit" ref="searchButton"
                                                :loading="loading">
                                                Search
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-form>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>

        <!-- Report Table -->
        <v-container fluid style="margin-top: -20px">
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="reportPanel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header"
                                expand-icon="mdi-chevron-down">
                                <v-icon class="me-2">mdi-table</v-icon>
                                Sewing Output Report Data
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field v-model="search" append-inner-icon="mdi-magnify" label="Search"
                                            single-line hide-details density="compact" variant="outlined"
                                            bg-color="grey-lighten-4" class="pl-2" clearable></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="success"
                                                    @click="exportToCSV">
                                                    CSV
                                                </v-btn>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="primary"
                                                    @click="exportToExcel">
                                                    EXCEL
                                                </v-btn>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="red"
                                                    @click="exportToPDF">
                                                    PDF
                                                </v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-row class="pt-0">
                                    <v-col cols="12">
                                        <div class="scrollable-table-wrapper">
                                            <v-data-table :headers="headers" :items="reportData" :loading="loading"
                                                v-model:items-per-page="itemsPerPage" :search="search" :hover="true"
                                                density="compact" class="responsive-table">
                                                <template v-slot:item.index="{ index }">
                                                    {{ ++index }}
                                                </template>

                                                <template v-slot:item.code="{ item }">
                                                    {{ item.code || "-" }}
                                                </template>

                                                <template v-slot:item.line="{ item }">
                                                    {{ item.line?.code || "-" }}
                                                </template>

                                                <template v-slot:item.bundle="{ item }">
                                                    {{ item.bundle?.code || "-" }}
                                                </template>

                                                <template v-slot:item.po="{ item }">
                                                    {{ item.bundle?.cutting?.po?.code || "-" }}
                                                </template>

                                                <template v-slot:item.style="{ item }">
                                                    {{ item.bundle?.cutting?.po?.style?.code || "-" }}
                                                </template>

                                                <template v-slot:item.output_qty="{ item }">
                                                    {{ item.output_qty || 0 }}
                                                </template>

                                                <template v-slot:item.reject_qty="{ item }">
                                                    {{ item.reject_qty || 0 }}
                                                </template>

                                                <template v-slot:item.recorded_at="{ item }">
                                                    {{ item.recorded_at ? new Date(item.recorded_at).toLocaleString() :
                                                    "-" }}
                                                </template>

                                                <template v-slot:item.status="{ item }">
                                                    <v-chip size="small" :color="item.status == 1 ? 'success' : 'red'">
                                                        {{ item.status == 1 ? "Active" : "Inactive" }}
                                                    </v-chip>
                                                </template>

                                                <template v-slot:no-data>
                                                    <div class="text-center py-4">
                                                        <p>No sewing output data found</p>
                                                    </div>
                                                </template>

                                            </v-data-table>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>

    </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import Axios from "axios";
import { prefix } from "@/store/api";
import formattedDate from '@/plugins/dateFormatter';

export default {
    name: "SewingOutputReport",

    data() {
        return {
            panel: [0],
            reportPanel: [0],
            loading: false,
            search: "",
            itemsPerPage: 10,
            reportData: [],
            headers: [
                {
                    title: "#SL",
                    align: "center",
                    key: "index",
                    width: "80px",
                    sortable: false,
                },
                {
                    title: "Output Code",
                    key: "code",
                    align: "left",
                    sortable: true,
                },
                {
                    title: "Sewing Line",
                    key: "line",
                    align: "left",
                    sortable: false,
                },
                {
                    title: "Bundle Code",
                    key: "bundle",
                    align: "left",
                    sortable: false,
                },
                {
                    title: "PO Code",
                    key: "po",
                    align: "left",
                    sortable: true,
                },
                {
                    title: "Style",
                    key: "style",
                    align: "center",
                    sortable: false,
                },
                {
                    title: "Output Quantity",
                    key: "output_qty",
                    align: "center",
                    sortable: false,
                },
                {
                    title: "Reject Quantity",
                    key: "reject_qty",
                    align: "center",
                    sortable: false,
                },
                {
                    title: "Recorded At",
                    key: "recorded_at",
                    align: "center",
                    sortable: true,
                },

                {
                    title: "Status",
                    key: "status",
                    align: "center",
                    sortable: true,
                },
            ],
        };
    },

    setup() {
        const store = useStore();

        const { handleSubmit } = useForm({
            validationSchema: yup.object({
                line_id: yup.number().nullable(),
                style_id: yup.number().nullable(),
                from_date: yup.date().nullable(),
                to_date: yup.date().nullable(),
            }),
        });

        const line_id = useField("line_id");
        const style_id = useField("style_id");
        const from_date = useField("from_date");
        const to_date = useField("to_date");

        return {
            line_id,
            style_id,
            from_date,
            to_date,
        };
    },

    computed: {
        sewingLineList() {
            return this.$store.getters.getProductionSewingLineList || [];
        },
        styleList() {
            return this.$store.getters.getProductionStyleList || [];
        },
    },

    methods: {
        loadReport() {
            this.loading = true;
            const params = {};

            if (this.line_id.value.value) {
                params.line_id = this.line_id.value.value;
            }

            if (this.style_id.value.value) {
                params.style_id = this.style_id.value.value;
            }

            if (this.from_date.value.value) {
                params.from_date = formattedDate(this.from_date.value.value);
            }

            if (this.to_date.value.value) {
                params.to_date = formattedDate(this.to_date.value.value);
            }

            Axios.get(`${prefix}production/reports/sewing-output`, { params })
                .then((response) => {
                    this.reportData = response.data.data || [];
                    if (this.reportData.length > 0) {
                        toast.success("Report loaded successfully!");
                    } else {
                        toast.info("No data found for the selected criteria");
                    }
                })
                .catch((err) => {
                    toast.error(err.response?.data?.message || "Failed to load report");
                    this.reportData = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        exportToCSV() {
            toast.info("CSV export functionality to be implemented");
        },
        exportToExcel() {
            toast.info("Excel export functionality to be implemented");
        },
        exportToPDF() {
            toast.info("PDF export functionality to be implemented");
        },
    },

    created() {
        // Load required data
        if (this.$store.getters.getProductionSewingLineList.length === 0) {
            this.$store.dispatch("getProductionSewingLineList");
        }
        if (this.$store.getters.getProductionStyleList.length === 0) {
            this.$store.dispatch("getProductionStyleList");
        }
    },
};
</script>

<style scoped>
.scrollable-table-wrapper {
    overflow-x: auto;
}

.responsive-table {
    min-width: 100%;
}
</style>